
import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface Team {
  id: string;
  name: string;
  description: string | null;
  status: string | null;
  created_at: string;
  updated_at: string;
  image_url?: string;
  team_members_count?: number;
  reports_count?: number;
  missions_count?: number;
  applications_count?: number;
}

interface DeleteTeamResponse {
  success: boolean;
  team_id: string;
  deleted_counts?: Record<string, number>;
  error?: string;
}

export const useTeamActions = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const refetchTeams = useCallback(async () => {
    const { data: teams, error } = await supabase
      .from("teams")
      .select(`
        id,
        name,
        description,
        status,
        created_at,
        updated_at,
        image_url,
        team_members_count:team_members(count),
        reports_count:reports(count),
        missions_count:missions(count),
        applications_count:applications(count)
      `)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching teams:", error);
      throw error;
    }

    console.log("Teams refetched with logos:", teams);
    return teams;
  }, []);

  const createTeam = async (teamData: Omit<Team, 'id' | 'created_at' | 'updated_at'>) => {
    setIsCreating(true);
    try {
      const { data: newTeam, error } = await supabase
        .from("teams")
        .insert([teamData])
        .select()
        .single();

      if (error) {
        console.error("Error creating team:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to create team",
        });
        return null;
      }

      toast({
        title: "Success",
        description: "Team created successfully",
      });

      await refetchTeams();
      navigate(`/teams`);
      return newTeam as Team;
    } catch (error) {
      console.error("Error creating team:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create team",
      });
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const deleteTeam = async (team: Team) => {
    setIsDeleting(true);
    try {
      console.log(`Attempting to delete team ${team.name} (${team.id}) with all dependencies`);
      
      const { data, error } = await supabase.rpc('delete_team_with_dependencies', {
        team_id_param: team.id
      });

      if (error) {
        console.error("Error deleting team:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to delete team: ${error.message}`,
        });
        return false;
      }

      // Type assertion to properly handle the response - first convert to unknown, then to our type
      const response = data as unknown as DeleteTeamResponse;

      if (!response?.success) {
        console.error("Team deletion failed:", response?.error);
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to delete team: ${response?.error || 'Unknown error'}`,
        });
        return false;
      }

      console.log("Team deletion successful:", response);
      
      // Show detailed deletion summary
      const deletedCounts = response.deleted_counts || {};
      const totalDeleted = Object.values(deletedCounts).reduce((sum: number, count: number) => sum + (count || 0), 0);
      
      toast({
        title: "Team Deleted Successfully",
        description: `Removed team "${team.name}" and ${totalDeleted} related records`,
      });

      await refetchTeams();
      return true;
    } catch (error) {
      console.error("Error deleting team:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete team",
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    createTeam,
    deleteTeam,
    refetchTeams,
    isCreating,
    isDeleting,
  };
};
